import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("h3", { class: "text-sm font-semibold text-gray-900" }, "Freshchat Support Default"),
  /*#__PURE__*/_createElementVNode("p", { class: "mt-1 text-sm text-gray-500" }, " Control the default Freshchat access for this school group. Individual schools can override this setting. ")
], -1)
const _hoisted_4 = { class: "flex items-center gap-3" }
const _hoisted_5 = {
  key: 0,
  class: "mt-3 text-sm text-red-600"
}
const _hoisted_6 = {
  key: 1,
  class: "mt-3 text-sm text-green-600"
}

import { computed, ref, watch } from "vue";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import { useSchoolGroupStore } from "@/stores/schoolGroupsStore";
import { useFilterStore } from "@/stores/filterStore";
import type { SchoolGroup } from "@/interfaces/school-group.interface";
import type { SchoolGroupFilterOption } from "@/interfaces/filters.interface";
import { showToast } from "@/utils/toastUtils";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/vue/20/solid";

type SchoolGroupLike = (SchoolGroup | SchoolGroupFilterOption) & {
  settings?: { freshchat?: boolean };
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SchoolGroupFreshchatSettingsCard',
  setup(__props) {

const schoolGroupStore = useSchoolGroupStore();
const filterStore = useFilterStore();

const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

const currentSchoolGroup = computed<SchoolGroupLike | null>(() => {
  if (schoolGroupStore.activeSchoolGroup) {
    return schoolGroupStore.activeSchoolGroup as SchoolGroupLike;
  }

  const selected = filterStore.selectedSchoolGroup;
  return selected ? (selected as SchoolGroupLike) : null;
});

const localValue = ref(false);

watch(
  currentSchoolGroup,
  (group) => {
    localValue.value = group?.settings?.freshchat ?? false;
  },
  { immediate: true }
);

const updateFreshchatSetting = async (enabled: boolean) => {
  const groupId =
    currentSchoolGroup.value?._id || filterStore.selectedFilters.schoolGroup || "";

  if (!groupId) {
    errorMessage.value = "Select a school group to update Freshchat settings.";
    localValue.value = false;
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  successMessage.value = "";

  const result = await schoolGroupStore.updateSchoolGroup(groupId, {
    settings: { freshchat: enabled },
  });

  loading.value = false;

  if (result) {
    filterStore.updateSchoolGroupSettings(result._id, result.settings);
    successMessage.value = enabled
      ? "Freshchat enabled for this school group."
      : "Freshchat disabled for this school group.";
    showToast({
      text: successMessage.value,
      type: "success",
      icon: CheckCircleIcon,
    });
    localValue.value = result.settings?.freshchat ?? false;
  } else {
    errorMessage.value = "Unable to update Freshchat settings. Please try again.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon,
    });
    localValue.value = currentSchoolGroup.value?.settings?.freshchat ?? false;
  }
};

const onToggle = async (value: boolean) => {
  if (loading.value || !currentSchoolGroup.value) {
    return;
  }

  localValue.value = value;
  await updateFreshchatSetting(value);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createVNode(_unref(SwitchGroup), null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(SwitchLabel), { class: "text-sm font-medium text-gray-700" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(localValue.value ? 'Enabled' : 'Disabled'), 1)
              ]),
              _: 1
            }),
            _createVNode(_unref(Switch), {
              "model-value": localValue.value,
              disabled: loading.value || !currentSchoolGroup.value,
              "onUpdate:modelValue": onToggle,
              class: _normalizeClass([
              localValue.value ? 'bg-blue-600' : 'bg-gray-300',
              loading.value || !currentSchoolGroup.value
                ? 'opacity-60 cursor-not-allowed'
                : 'cursor-pointer',
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2'
            ])
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", {
                  "aria-hidden": "true",
                  class: _normalizeClass([
                localValue.value ? 'translate-x-6' : 'translate-x-1',
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform'
              ])
                }, null, 2)
              ]),
              _: 1
            }, 8, ["model-value", "disabled", "class"])
          ])
        ]),
        _: 1
      })
    ]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(errorMessage.value), 1))
      : (successMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(successMessage.value), 1))
        : _createCommentVNode("", true)
  ]))
}
}

})